import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patchSeparator, SeparatorQueryKey } from "api/separator.api";
import Modal from "components/Modal";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DropdownFormProvider } from "components/DropdownFormProvider";
import {
  FacilityType,
  getWaterMeasurementLengthLabel,
  getWaterMeasurementPointLabel,
  getWaterMeasurementReportingUnitLabel,
  getWaterMeasurementVehicleTypeLabel,
  getWaterMeterExistsLabel,
  WaterMeasurementLength,
  WaterMeasurementPoint,
  WaterMeasurementPointData,
  WaterMeasurementReportingUnit,
  WaterMeasurementVehicleType,
  WaterMeterExists,
} from "types/separator/waterMeasurementPointData.type";
import { TextAreaFormProvider } from "components/TextAreaFormProvider";
import { TextInputFormProvider } from "components/TextInputFormProvider";
import Divider from "components/Divider";
import { LabeledDivider } from "components/LabeledDivider";
import {
  ModalPrimaryButton,
  ModalSecondaryAction,
} from "../customer/AddFacility/AddFacilityModal";
import { useNotification } from "hooks/useNotification";
import {
  PatchSeparatorDTO,
  patchSeparatorDTOSchema,
} from "types/separator/separator.type";
import { useCallback, useEffect } from "react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  separatorId: number;
  waterMeasurementPointData: WaterMeasurementPointData;
};

export const EditTechnicalInformationModal = ({
  isOpen,
  onClose,
  separatorId,
  waterMeasurementPointData,
}: Props) => {
  const form = useForm<PatchSeparatorDTO>({
    resolver: zodResolver(patchSeparatorDTOSchema),
    defaultValues: {
      waterMeasurementPointData,
    },
  });

  const facilityType = form.watch("waterMeasurementPointData.facilityType");
  const isVehicleWash = facilityType === FacilityType.VehicleWash;

  const waterMeasurementLength = form.watch(
    "waterMeasurementPointData.waterMeasurementLength",
  );
  const measurementPoint = form.watch(
    "waterMeasurementPointData.measurementPoint",
  );
  const vehicleType = form.watch("waterMeasurementPointData.vehicleType");

  const notification = useNotification();
  const queryClient = useQueryClient();
  const patchSeparatorMutation = useMutation({
    ...patchSeparator(),
    onSuccess: () => {
      notification.changesSaved();
      onClose();
      queryClient.invalidateQueries({ queryKey: [SeparatorQueryKey.GetOne] });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });

  const onSubmit = form.handleSubmit((data: PatchSeparatorDTO) => {
    patchSeparatorMutation.mutate({ separatorId, payload: data });
  });

  const resetForm = useCallback(
    (data?: WaterMeasurementPointData) => {
      form.reset({
        waterMeasurementPointData: data,
      });
    },
    [form],
  );

  useEffect(() => {
    resetForm(waterMeasurementPointData);
  }, [waterMeasurementPointData, resetForm]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      afterClose={() => {
        // afterClose fires from the frozen (pre-close) render, so any prop
        // captured here is stale — reset to the defaults the effect maintains
        form.reset();
      }}
      title="Ändra teknisk information"
      hideButtons
    >
      <FormProvider {...form}>
        <form className="mt-4 pt-4" onSubmit={onSubmit}>
          <div className="mt-2 grid grid-cols-1 gap-8 sm:grid-cols-1">
            <DropdownFormProvider
              label="Provlängd"
              name="waterMeasurementPointData.waterMeasurementLength"
              items={[
                {
                  label: getWaterMeasurementLengthLabel(
                    WaterMeasurementLength.Stickprov,
                  ),
                  value: WaterMeasurementLength.Stickprov,
                },
                {
                  label: getWaterMeasurementLengthLabel(
                    WaterMeasurementLength.SixHours,
                  ),
                  value: WaterMeasurementLength.SixHours,
                },
                {
                  label: getWaterMeasurementLengthLabel(
                    WaterMeasurementLength.TwentyFourHours,
                  ),
                  value: WaterMeasurementLength.TwentyFourHours,
                },
                {
                  label: getWaterMeasurementLengthLabel(
                    WaterMeasurementLength.Week,
                  ),
                  value: WaterMeasurementLength.Week,
                },
                {
                  label: getWaterMeasurementLengthLabel(
                    WaterMeasurementLength.Other,
                  ),
                  value: WaterMeasurementLength.Other,
                },
              ]}
            />
            {waterMeasurementLength === WaterMeasurementLength.Other && (
              <TextInputFormProvider
                label="Ange annan provlängd"
                name="waterMeasurementPointData.waterMeasurementLengthOther"
              />
            )}
            <DropdownFormProvider
              label="Provtagningspunkt"
              name="waterMeasurementPointData.measurementPoint"
              items={[
                {
                  label: getWaterMeasurementPointLabel(
                    WaterMeasurementPoint.InAvskiljare,
                  ),
                  value: WaterMeasurementPoint.InAvskiljare,
                },
                {
                  label: getWaterMeasurementPointLabel(
                    WaterMeasurementPoint.Provtagningsbrunn,
                  ),
                  value: WaterMeasurementPoint.Provtagningsbrunn,
                },
                {
                  label: getWaterMeasurementPointLabel(
                    WaterMeasurementPoint.AfterSupplementaryCleaning,
                  ),
                  value: WaterMeasurementPoint.AfterSupplementaryCleaning,
                },
                {
                  label: getWaterMeasurementPointLabel(
                    WaterMeasurementPoint.Other,
                  ),
                  value: WaterMeasurementPoint.Other,
                },
              ]}
            />
            {measurementPoint === WaterMeasurementPoint.Other && (
              <TextInputFormProvider
                label="Ange annan provtagningspunkt"
                name="waterMeasurementPointData.measurementPointOther"
              />
            )}
            <TextAreaFormProvider
              label="Analysparametrar eller provpaket"
              name="waterMeasurementPointData.analyses"
              rows={3}
            />
            <TextAreaFormProvider
              label="Gränsvärden"
              name="waterMeasurementPointData.limits"
              rows={3}
            />

            <DropdownFormProvider
              label="Typ av verksamhet"
              name="waterMeasurementPointData.facilityType"
              items={[
                {
                  label: "Fordonstvätt",
                  value: FacilityType.VehicleWash,
                },
                {
                  label: "Annan",
                  value: FacilityType.Other,
                },
              ]}
            />
            {facilityType === FacilityType.Other && (
              <TextInputFormProvider
                label="Ange annan verksamhet"
                name="waterMeasurementPointData.facilityTypeOther"
              />
            )}
            {isVehicleWash && (
              <>
                <LabeledDivider label="Fordonstvätt" className="-my-2" />
                <DropdownFormProvider
                  label="Vattenmätare"
                  name="waterMeasurementPointData.waterMeterExists"
                  items={[
                    {
                      label: getWaterMeterExistsLabel(WaterMeterExists.Yes),
                      value: WaterMeterExists.Yes,
                    },
                    {
                      label: getWaterMeterExistsLabel(WaterMeterExists.No),
                      value: WaterMeterExists.No,
                    },
                    {
                      label: getWaterMeterExistsLabel(WaterMeterExists.Unknown),
                      value: WaterMeterExists.Unknown,
                    },
                  ]}
                />
                <DropdownFormProvider
                  label="Redovisning"
                  name="waterMeasurementPointData.reporting"
                  items={[
                    {
                      label: getWaterMeasurementReportingUnitLabel(
                        WaterMeasurementReportingUnit.PerLiter,
                      ),
                      value: WaterMeasurementReportingUnit.PerLiter,
                    },
                    {
                      label: getWaterMeasurementReportingUnitLabel(
                        WaterMeasurementReportingUnit.PerVehicle,
                      ),
                      value: WaterMeasurementReportingUnit.PerVehicle,
                    },
                  ]}
                />
                <DropdownFormProvider
                  label="Fordonstyp"
                  name="waterMeasurementPointData.vehicleType"
                  items={[
                    {
                      label: getWaterMeasurementVehicleTypeLabel(
                        WaterMeasurementVehicleType.Car,
                      ),
                      value: WaterMeasurementVehicleType.Car,
                    },
                    {
                      label: getWaterMeasurementVehicleTypeLabel(
                        WaterMeasurementVehicleType.Truck,
                      ),
                      value: WaterMeasurementVehicleType.Truck,
                    },
                    {
                      label: getWaterMeasurementVehicleTypeLabel(
                        WaterMeasurementVehicleType.Train,
                      ),
                      value: WaterMeasurementVehicleType.Train,
                    },
                    {
                      label: getWaterMeasurementVehicleTypeLabel(
                        WaterMeasurementVehicleType.Machines,
                      ),
                      value: WaterMeasurementVehicleType.Machines,
                    },
                    {
                      label: getWaterMeasurementVehicleTypeLabel(
                        WaterMeasurementVehicleType.Mixed,
                      ),
                      value: WaterMeasurementVehicleType.Mixed,
                    },
                    {
                      label: getWaterMeasurementVehicleTypeLabel(
                        WaterMeasurementVehicleType.Other,
                      ),
                      value: WaterMeasurementVehicleType.Other,
                    },
                  ]}
                />
                {vehicleType === WaterMeasurementVehicleType.Other && (
                  <TextInputFormProvider
                    label="Ange annan fordonstyp"
                    name="waterMeasurementPointData.vehicleTypeOther"
                  />
                )}
              </>
            )}
          </div>
          <Divider className="mb-4 mt-6" />
          <div className="flex justify-end">
            <div className="flex w-96 gap-x-2">
              <ModalSecondaryAction onClick={onClose} />
              <ModalPrimaryButton
                isLoading={patchSeparatorMutation.isPending}
                buttonText="Spara"
                loadingText="Sparar"
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
